import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchAssets, fetchAsset, createAsset, updateAssetStatus, updateAsset, resolveAssetByBarcode,
} from '@/services/assetService';
import type { Asset, AssetStatus } from '@/types/models';

export function useAssets() {
  return useQuery({
    queryKey: ['assets'],
    queryFn: fetchAssets,
    staleTime: 30_000,
  });
}

export function useAsset(id: string | undefined) {
  return useQuery({
    queryKey: ['assets', id],
    queryFn: () => fetchAsset(id!),
    enabled: !!id,
  });
}

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createAsset,
    onSuccess: (asset) => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['assets', 'work-order', asset.work_order_id] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateAssetStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: AssetStatus }) =>
      updateAssetStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Asset> }) =>
      updateAsset(id, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useResolveAsset(barcode: string | undefined) {
  return useQuery({
    queryKey: ['assets', 'resolve', barcode],
    queryFn: () => resolveAssetByBarcode(barcode!),
    enabled: !!barcode,
  });
}
